(() => {
  const LAYER_SELECTOR = '[data-product-card-video]';
  const CARD_SELECTOR = 'product-card';
  const hoverQuery = window.matchMedia('(hover: hover) and (pointer: fine)');
  const reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
  const visibilityRatios = new Map();
  const initializedLayers = new WeakSet();
  let activeMobileLayer = null;
  let selectionFrame = 0;

  const playbackAllowed = () => {
    return !reducedMotionQuery.matches && !(connection && connection.saveData);
  };

  const getVideo = (layer) => layer && layer.querySelector('video');

  const stopLayer = (layer, reset = true) => {
    const video = getVideo(layer);
    if (!video) return;

    video.pause();
    layer.classList.remove('is-playing');

    if (reset) {
      try {
        video.currentTime = 0;
      } catch (error) {
        // The video might not have loaded metadata yet.
      }
    }
  };

  const playLayer = (layer) => {
    if (!layer || !playbackAllowed() || document.hidden) return;

    const video = getVideo(layer);
    if (!video) return;

    video.muted = true;
    video.playsInline = true;

    const playPromise = video.play();
    if (playPromise && typeof playPromise.catch === 'function') {
      playPromise.catch(() => {
        layer.classList.remove('is-playing');
      });
    }
  };

  const stopActiveMobileLayer = () => {
    if (!activeMobileLayer) return;
    stopLayer(activeMobileLayer);
    activeMobileLayer = null;
  };

  const selectMobileLayer = () => {
    selectionFrame = 0;

    if (hoverQuery.matches || !playbackAllowed() || document.hidden) {
      stopActiveMobileLayer();
      return;
    }

    const viewportCenterX = window.innerWidth / 2;
    const viewportCenterY = window.innerHeight / 2;
    let bestLayer = null;
    let bestScore = Number.POSITIVE_INFINITY;

    visibilityRatios.forEach((ratio, layer) => {
      if (!layer.isConnected || ratio < 0.6) return;

      const rect = layer.getBoundingClientRect();
      const centerX = rect.left + rect.width / 2;
      const centerY = rect.top + rect.height / 2;
      const verticalDistance = Math.abs(centerY - viewportCenterY);
      const horizontalDistance = Math.abs(centerX - viewportCenterX);
      const score = verticalDistance + horizontalDistance * 0.2 - ratio * 100;

      if (score < bestScore) {
        bestScore = score;
        bestLayer = layer;
      }
    });

    if (bestLayer === activeMobileLayer) return;

    stopActiveMobileLayer();
    activeMobileLayer = bestLayer;
    playLayer(activeMobileLayer);
  };

  const scheduleMobileSelection = () => {
    if (selectionFrame) return;
    selectionFrame = window.requestAnimationFrame(selectMobileLayer);
  };

  const visibilityObserver = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        visibilityRatios.set(entry.target, entry.isIntersecting ? entry.intersectionRatio : 0);
      });
      scheduleMobileSelection();
    },
    {
      root: null,
      threshold: [0, 0.25, 0.6, 0.75, 0.9, 1],
    }
  );

  const initializeLayer = (layer) => {
    if (initializedLayers.has(layer)) return;

    const video = getVideo(layer);
    const card = layer.closest(CARD_SELECTOR);
    if (!video || !card) return;

    initializedLayers.add(layer);
    video.muted = true;
    video.playsInline = true;

    video.addEventListener('playing', () => {
      layer.classList.add('is-playing');
    });

    video.addEventListener('pause', () => {
      layer.classList.remove('is-playing');
    });

    card.addEventListener('mouseenter', () => {
      if (!hoverQuery.matches) return;
      playLayer(layer);
    });

    card.addEventListener('mouseleave', () => {
      if (!hoverQuery.matches) return;
      stopLayer(layer);
    });

    visibilityObserver.observe(layer);
  };

  const initializeWithin = (root) => {
    if (!root) return;

    if (root.matches && root.matches(LAYER_SELECTOR)) {
      initializeLayer(root);
    }

    if (root.querySelectorAll) {
      root.querySelectorAll(LAYER_SELECTOR).forEach(initializeLayer);
    }
  };

  const stopAllLayers = () => {
    document.querySelectorAll(LAYER_SELECTOR).forEach((layer) => stopLayer(layer));
    activeMobileLayer = null;
  };

  const handlePlaybackPreferenceChange = () => {
    stopAllLayers();
    scheduleMobileSelection();
  };

  const init = () => {
    initializeWithin(document);

    const mainContent = document.getElementById('MainContent');
    if (mainContent) {
      const mutationObserver = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === Node.ELEMENT_NODE) {
              initializeWithin(node);
            }
          });
        });
      });

      mutationObserver.observe(mainContent, {
        childList: true,
        subtree: true,
      });
    }

    window.addEventListener('resize', scheduleMobileSelection, { passive: true });
    window.addEventListener('orientationchange', scheduleMobileSelection);

    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        stopAllLayers();
      } else {
        scheduleMobileSelection();
      }
    });

    document.addEventListener('shopify:section:load', (event) => {
      initializeWithin(event.target);
      scheduleMobileSelection();
    });

    if (typeof hoverQuery.addEventListener === 'function') {
      hoverQuery.addEventListener('change', handlePlaybackPreferenceChange);
      reducedMotionQuery.addEventListener('change', handlePlaybackPreferenceChange);
    } else {
      hoverQuery.addListener(handlePlaybackPreferenceChange);
      reducedMotionQuery.addListener(handlePlaybackPreferenceChange);
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
